import { h, Fragment } from 'preact';
import { useMemo } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import PlusIcon from 'utils/icon/icon-plus-alt.svg';
import MinusIcon from 'utils/icon/icon-minus.svg';
import Image from 'utils/image';
import { isLauraCanada, locale } from 'utils/helpers';
import { withCartContext } from './cart-context';

const CartItem = (props) => {
  const { cart, line } = props;
  let maxQuantity =
    line.merchandise?.quantityAvailable ||
    line.quantity_rule?.maxQuantity ||
    Infinity;

  if (line.children) {
    // If bundle, get lowest child quantity
    maxQuantity = line.children.reduce((prevQuantity, child) => {
      const childMax =
        (cart.mode === 'storefront'
          ? child.merchandise?.quantityAvailable
          : child.quantity_rule?.maxQuantity) || Infinity;
      return childMax < prevQuantity ? childMax : prevQuantity;
    }, Infinity);
  }

  const handleAdjustQuantity = (quantity) => {
    const qty = quantity > maxQuantity ? maxQuantity : quantity;
    let payload = [
      {
        id: line.id,
        quantity: qty,
      },
    ];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => ({
        id: child.id,
        attributes: child.attributes,
        quantity: qty,
      }));
    }

    return cart.update(payload);
  };

  const handleRemove = () => {
    let payload = [line.id];

    // If bundle, add children to update request
    if (line.children) {
      payload = line.children.map((child) => child.id);
    }

    return cart.remove(payload);
  };
  const lineItem = useMemo(() => {   

    let label = '';
    if (line.properties?._cart_label) {
      const labels = line.properties._cart_label.split(",");
      label = locale.includes("fr") && labels[1] ? labels[1] : labels[0];
    }

    const variant = cart.productsData[line.product_id]?.variants?.edges.find(
      (item) =>
        item.node.id === `gid://shopify/ProductVariant/${line.variant_id}`,
    )?.node;

    const options = line.options_with_values
      .filter(
        (option) =>
          !(
            !isLauraCanada() &&
            option.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.fit
          ) && option.name !== 'Title',
      )
      .map((option) => option.value)
      .join(', ');
    return (
      <div
        key={line.id}
        className="flex gap-12 ml:gap-8 py-16 border-b border-grey-300"
      >
        <a
          className="flex-none"
          href={
            cart.mode === 'storefront'
              ? `/products/${line.merchandise.product.handle}`
              : line.url
          }
        >
          <Image
            src={line.image}
            alt={line.product_title}
            classes="w-96 h-auto"
          />
        </a>
        <div className="flex-1 flex flex-col gap-10 min-w-0">
          <div className="flex gap-8">
            <div className="flex-1 min-w-0">
              <a
                className="truncate body-2 block mb-4"
                href={
                  cart.mode === 'storefront'
                    ? `/products/${line.merchandise.product.handle}`
                    : line.url
                }
              >
                {cart.mode === 'storefront'
                  ? line.merchandise?.product.title
                  : line.product_title}
              </a>
              {!!options.length && (
                <div className="body-3 lc:text-subdued ml:text-misc-sale">
                  {options}
                </div>
              )}
              {!!label && (
                <div className="body-3 text-error lc:text-misc-sale mt-4">
                  {label}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="body-2-semibold">
                {cart.mode === 'storefront'
                  ? formatMoney(line.cost.totalAmount || { amount: 0 })
                  : formatMoney({ amount: line.line_price }, true)}
              </div>
              {line.original_line_price > line.line_price ? (
                <div className="body-3 line-through lc:text-misc-sale ml:text-error">
                  {cart.mode === 'storefront'
                    ? formatMoney(line.original_line_price || { amount: 0 })
                    : formatMoney({ amount: line.original_line_price }, true)}
                </div>
              ) : (
                <>
                  {!!variant &&
                    parseFloat(variant.compareAtPrice?.amount) * 100 >
                      line.price && (
                      <div className="body-3 line-through lc:text-misc-sale ml:text-error">
                        {formatMoney(
                          {
                            amount:
                              parseFloat(variant.compareAtPrice?.amount) *
                              line.quantity,
                            currencyCode:
                              variant.compareAtPrice.amount.currencyCode,
                          },
                          false,
                          true,
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
          <div className="mt-auto flex gap-16 ml:gap-20 items-center">
            <div className="relative">
              <button
                className="absolute left-0 ml:left-4 lc:px-12 flex justify-center items-center h-full"
                onClick={() => handleAdjustQuantity(line.quantity - 1)}
                aria-label={window.TMW.Strings.cart.remove}
              >
                <MinusIcon className="w-12 h-auto ml:w-16" />
              </button>
              <div className="p-8 h-[35px] w-[88px] ml:h-[27px] ml:w-[77px] flex items-center justify-center body-3 lc:border-grey-300 rounded-4 lc:border">
                {line.quantity}
              </div>
              <button
                className="absolute top-0 ml:right-4 right-0 lc:px-12 flex justify-center items-center h-full"
                onClick={() => handleAdjustQuantity(line.quantity + 1)}
                aria-label={window.TMW.Strings.cart.add}
              >
                <PlusIcon className="w-12 h-auto ml:w-16" />
              </button>
            </div>
            <button
              onClick={() => handleRemove()}
              className="body-3-underline hover:no-underline"
            >
              {window.TMW.Strings.cart.remove}
            </button>
          </div>
        </div>
      </div>
    );
  }, [line, cart.productsData]);

  return lineItem;
};

export default withCartContext(CartItem);
